﻿import React, {JSX} from "react";
import Icon from "../Icon";
import {HtmlRequiredPrefixedControlProps} from "./HtmlControlProps";
import LabeledControl from "./LabeledControl";
import {useLabeledControl} from "./LabeledControlData";

export interface FileInputProps extends Omit<HtmlRequiredPrefixedControlProps<HTMLInputElement>, "value"> {
	value?: FileList | null | undefined;
	multiple?: boolean;
	accept?: string;
	capture?: "user" | "environment" | undefined;
	help?: FileInputHelp;
}

export interface FileInputHelp {
	single?: string;
	multiple?: string;
}

function getFileIcon(fileType: string): string {
	if (fileType.includes("pdf")) return "file-pdf";
	if (fileType.includes("jpg") || fileType.includes("jpeg") || fileType.includes("png")) return "file-image";
	return "file-general";
}

function isEmptyFileList(fileList?: FileList | null): fileList is null | undefined {
	return fileList == null || fileList.length === 0;
}

export default function FileInput(props: FileInputProps): JSX.Element {
	const {
		id,
		label,
		hint,
		help,
		column,
		controlPrefix,
		controlSuffix,
		name,
		value,
		hidden,
		onChange,
		onBlur,
		onClick,
		error,
		inlineError,
		required,
		disabled,
		ref,
		multiple,
		accept,
		capture
	} = props;

	const {componentId, ariaRef} = useLabeledControl(id, hint, error);

	if (hidden) {
		return <></>;
	}

	return <LabeledControl label={label} type="file" id={componentId} required={required} column={column} error={error} inlineError={inlineError} disabled={disabled}
	                       clickable={disabled && onClick != null}
	                       hint={hint} hidden={hidden} ariaRef={ariaRef} controlPrefix={controlPrefix}
	                       controlSuffix={controlSuffix}>
		<input
			id={componentId}
			type="file"
			ref={ref}
			onChange={onChange}
			onBlur={onBlur}
			onClick={onClick}
			name={name}
			required={required}
			aria-describedby={ariaRef.current?.describedBy}
			aria-invalid={ariaRef.current?.invalid}
			aria-required={required}
			readOnly={disabled}
			tabIndex={disabled ? -1 : undefined}
			disabled={disabled && onClick == null}
			multiple={multiple}
			accept={accept}
			capture={capture}/>

		<span className="field__input__file-list">{
			isEmptyFileList(value)
				? <span className="field__input__file-list--choose">
					<Icon icon="input-file-upload"/>
					<span>{multiple ? help?.multiple ?? "Select/drop one or more files" : help?.single ?? "Choose/drop a file"}
					</span>
			</span>
				: <span className="field__input__file-list--files">
					{Array.from(value).map((item: File, index: number) => <span key={index}>
						<Icon icon={getFileIcon(item.type)}/>
							{item.name}
					</span>
					)}
				</span>
		}</span>
	</LabeledControl>;
}
